
import { useState, useEffect } from "react";
import { Camera, ImageIcon } from "lucide-react";
import Image from "@/components/ui/image";
import { ImageUploadDialog } from "./components/ImageUploadDialog";
import { useImageManagement } from "./hooks/useImageManagement";
import { useUserRole } from "@/hooks/useUserRole";
import { isSupabasePublicStorageUrl } from "@/utils/image-storage/remote";

interface FundImageUploadProps {
  fundId: string;
  currentImageUrl: string | null;
  onImageUpdate: (newImageUrl: string | null) => void;
  displayMode?: "full" | "thumbnail";
}

export function FundImageUpload({ 
  fundId, 
  currentImageUrl, 
  onImageUpdate,
  displayMode = "full" 
}: FundImageUploadProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const { hasRole } = useUserRole();
  const { isUploading, handleFileUpload, handleImageUrlUpdate, handleImageRemove } = useImageManagement({
    fundId,
    onImageUpdate
  });

  useEffect(() => {
    const checkRole = async () => {
      const hasAccess = await hasRole('sys_admin');
      setCanEdit(hasAccess);
    };
    checkRole();
  }, [hasRole]);

  const imageContainerClass = displayMode === "full" 
    ? "w-full h-[300px]" 
    : "w-[200px] h-[100px]";

  // Use raw public URL (no transformations)
  const isSupabaseImage = !!currentImageUrl && isSupabasePublicStorageUrl(currentImageUrl);
  const normalizedSrc = currentImageUrl;
  const sizes = displayMode === 'full' ? '100vw' : '200px';

  return (
    <div className="space-y-4">
      <div className={`relative ${imageContainerClass} overflow-hidden`}>
        {currentImageUrl ? (
          <div className="relative group h-full w-full overflow-hidden">
            <div className="absolute inset-0 w-full h-full">
              <Image 
                src={normalizedSrc}
                alt="Fund image"
                className="w-full h-full object-cover rounded-lg"
                enableOptimization={false}
                fallbackSrc={isSupabaseImage ? currentImageUrl || undefined : undefined}
                instant={displayMode === 'full'}
                priority={displayMode === 'full'}
              />
            </div>
            {canEdit && (
              <div 
                className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 rounded-lg cursor-pointer"
                onClick={() => setIsDialogOpen(true)}
              >
                <Camera className="h-5 w-5 text-white" />
              </div>
            )}
          </div>
        ) : (
          <div 
            className={`${imageContainerClass} bg-muted rounded-lg flex items-center justify-center group relative ${canEdit ? 'cursor-pointer' : ''}`}
            onClick={() => canEdit && setIsDialogOpen(true)}
          >
            <ImageIcon className="w-8 h-8 text-muted-foreground" />
            {canEdit && (
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg">
                <Camera className="h-5 w-5 text-white" />
              </div>
            )}
          </div>
        )}
      </div>

      {canEdit && (
        <ImageUploadDialog
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          onUploadUrl={handleImageUrlUpdate}
          onUploadFile={handleFileUpload}
          onRemove={handleImageRemove}
          currentImageUrl={currentImageUrl}
          isUploading={isUploading}
        />
      )}
    </div>
  );
}
